import React, { useEffect, useState, useCallback } from 'react';
import { theme } from '../theme';
import Modal from './ModalConversion';
import { Loading } from './Loading';
import { endpointGetNotActivatedReportItems, urlTasks } from '../services/Endpoints';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faChevronLeft, faChevronRight, faDownload } from '@fortawesome/free-solid-svg-icons';

const PER_PAGE = 50;

// sync_status meaning: 1 = Actualizado, 0 = Sin procesar, -1 = No encontrado en Inkit
const SYNC_STATUS_LABEL = {
    1: { label: 'Actualizado', cls: 'badge-status status-on' },
    0: { label: 'Sin procesar', cls: 'badge-status status-disabled' },
    '-1': { label: 'No encontrado', cls: 'badge-status status-off' },
};

const syncLabel = (val) => {
    const key = val != null ? String(val) : '';
    return SYNC_STATUS_LABEL[key] || { label: val ?? '—', cls: 'badge-status status-disabled' };
};

const formatIdentifiers = (ids = {}) => {
    if (!ids || typeof ids !== 'object') return '';
    return Object.entries(ids)
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]) => `${k}: ${v}`)
        .join(' | ') || '';
};

// Escapa un campo para CSV: envuelve en comillas si contiene coma, comilla o salto de línea
const csvEscape = (val) => {
    if (val == null || val === '') return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

/**
 * NotActivatedModal
 * Props:
 *   isOpen         boolean  — controls visibility
 *   onClose        fn       — close handler
 *   channel        string   — alias del canal (ej: 'amazon')
 *   channelLabel   string   — nombre legible del canal
 *   reportDate     string   — fecha del reporte (ISO)
 */
function NotActivatedModal({ isOpen, onClose, channel, channelLabel, reportDate }) {
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);

    const totalPages = Math.ceil(total / PER_PAGE);

    const fetchItems = useCallback(async (p = 1) => {
        if (!channel) return;
        try {
            setLoading(true);
            const url = `${urlTasks}${endpointGetNotActivatedReportItems}?channel=${channel}&page=${p}&per_page=${PER_PAGE}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setItems(res.data.items || []);
                setTotal(res.data.total || 0);
            } else {
                setItems([]);
                setTotal(0);
            }
        } catch (err) {
            console.error('Error fetching not-activated items:', err);
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [channel]);

    // Reset and fetch when modal opens or channel changes
    useEffect(() => {
        if (isOpen && channel) {
            setPage(1);
            fetchItems(1);
        }
        if (!isOpen) {
            setItems([]);
            setTotal(0);
            setPage(1);
        }
    }, [isOpen, channel, fetchItems]);

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        setPage(newPage);
        fetchItems(newPage);
    };

    const formatDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleString('es-MX', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    };

    // ── Exportar CSV ────────────────────────────────────────────────────────────
    const handleExportCsv = async () => {
        if (!channel || total === 0) return;
        try {
            setExporting(true);

            // Descarga todos los registros en una sola petición
            const url = `${urlTasks}${endpointGetNotActivatedReportItems}?channel=${channel}&page=1&per_page=${total}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            const res = await response.json();
            if (!res || res.status !== 'success') throw new Error('Error al obtener datos');

            const allItems = res.data.items || [];

            // Cabecera
            const headers = [
                'Identificadores',
                'Titulo',
                'Sello Editorial',
                'Status',
                'Sync Status',
                'Ultima Sincronizacion',
                'Link',
            ];

            const rows = allItems.map((item, idx) => {
                const badge = syncLabel(item.sync_status);
                return [
                    csvEscape(formatIdentifiers(item.identifiers)),
                    csvEscape(item.title),
                    csvEscape(item.imprint),
                    csvEscape(item.status),
                    csvEscape(badge.label),
                    csvEscape(formatDate(item.last_sync_date)),
                    csvEscape(item.link),
                ].join(',');
            });

            const csvContent = [headers.join(','), ...rows].join('\r\n');

            // BOM para que Excel lo abra bien con caracteres especiales
            const bom = '\uFEFF';
            const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
            const blobUrl = URL.createObjectURL(blob);

            // Nombre de archivo: sin_sincronizar_amazon_20260429.csv
            const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const fileName = `sin_sincronizar_${channel}_${dateStr}.csv`;

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('Error exportando CSV:', err);
            alert('Error al exportar CSV: ' + err.message);
        } finally {
            setExporting(false);
        }
    };
    // ────────────────────────────────────────────────────────────────────────────

    return (
        <Modal isOpen={isOpen} onClose={onClose} fullscreen>
            <div style={{ minHeight: 200 }}>
                {/* Header */}
                <div className="d-flex align-items-center gap-2 mb-1" style={{ flexWrap: 'wrap' }}>
                    <span style={{ color: '#ffc107', fontSize: '1.3rem' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                    </span>
                    <div>
                        <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>
                            Registros sin sincronizar
                        </h4>
                        <p className="mb-0" style={{ color: 'rgba(var(--ink-overlay-rgb),0.55)', fontSize: '0.85rem' }}>
                            Canal: <strong style={{ color: 'var(--ink-text)' }}>{channelLabel || channel}</strong>
                            {reportDate && (
                                <> &nbsp;·&nbsp; Reporte: <strong style={{ color: 'var(--ink-text)' }}>{formatDate(reportDate)}</strong></>
                            )}
                        </p>
                    </div>
                </div>

                {/* Badge + botón exportar */}
                {!loading && (
                    <div className="d-flex align-items-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
                        <span
                            className="badge-status status-off"
                            style={{ fontSize: '0.8rem', padding: '5px 12px' }}
                        >
                            {total.toLocaleString()} sin sincronizar
                        </span>

                        {total > 0 && (
                            <button
                                className="btn btn-outline-success btn-sm d-flex align-items-center gap-2"
                                onClick={handleExportCsv}
                                disabled={exporting}
                                title="Exportar todos los registros a CSV"
                            >
                                <FontAwesomeIcon icon={faDownload} spin={exporting} />
                                {exporting ? 'Exportando…' : 'Exportar CSV'}
                            </button>
                        )}
                    </div>
                )}

                {loading && <Loading />}

                {!loading && items.length === 0 && (
                    <div className="text-center py-5" style={{ color: 'rgba(var(--ink-overlay-rgb),0.45)' }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>✅</div>
                        <p>Todos los registros de este reporte fueron sincronizados correctamente.</p>
                    </div>
                )}

                {!loading && items.length > 0 && (
                    <>
                        <div className="modern-table-container">
                            <table className="modern-table table-hover">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: 60 }}>#</th>
                                        <th>Identificadores</th>
                                        <th>Título</th>
                                        <th>Sello Editorial</th>
                                        <th className="text-center" style={{ width: 90 }}>Status</th>
                                        <th className="text-center" style={{ width: 140 }}>Sync Status</th>
                                        <th style={{ width: 160 }}>Última Sync</th>
                                        <th>Link</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, idx) => {
                                        const badge = syncLabel(item.sync_status);
                                        const rowNum = (page - 1) * PER_PAGE + idx + 1;
                                        return (
                                            <tr key={idx}>
                                                <td className="text-center" style={{ color: 'rgba(var(--ink-overlay-rgb),0.35)', fontSize: '0.8rem' }}>
                                                    {item.row_index != null ? item.row_index : rowNum}
                                                </td>
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', wordBreak: 'break-all' }}>
                                                    {formatIdentifiers(item.identifiers) || <span style={{ opacity: 0.35 }}>—</span>}
                                                </td>
                                                <td style={{ fontSize: '0.85rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    title={item.title || undefined}>
                                                    {item.title || <span style={{ opacity: 0.35 }}>—</span>}
                                                </td>
                                                <td style={{ fontSize: '0.85rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    title={item.imprint || undefined}>
                                                    {item.imprint || <span style={{ opacity: 0.35 }}>—</span>}
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge-status ${item.status === 'on' ? 'status-on' : 'status-off'}`}
                                                        style={{ fontSize: '0.75rem', padding: '3px 8px' }}>
                                                        {item.status || '—'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={badge.cls} style={{ fontSize: '0.75rem', padding: '3px 8px' }}>
                                                        {badge.label}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', color: 'rgba(var(--ink-overlay-rgb),0.6)' }}>
                                                    {formatDate(item.last_sync_date) || '—'}
                                                </td>
                                                <td style={{ fontSize: '0.8rem', maxWidth: 200, wordBreak: 'break-all' }}>
                                                    {item.link ? (
                                                        <a
                                                            href={item.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: theme.primary, textDecoration: 'underline', wordBreak: 'break-all' }}
                                                            title={item.link}
                                                        >
                                                            Ver enlace
                                                        </a>
                                                    ) : (
                                                        <span style={{ opacity: 0.35 }}>—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Paginator */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page <= 1}
                                    title="Página anterior"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                                <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.7)', fontSize: '0.85rem' }}>
                                    Página {page} de {totalPages} &nbsp;·&nbsp; {total.toLocaleString()} registros
                                </span>
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page >= totalPages}
                                    title="Página siguiente"
                                >
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Footer */}
                <div className="d-flex justify-content-end mt-4 pt-3 border-top border-secondary">
                    <button className="btn btn-secondary px-4" onClick={onClose}>
                        Cerrar
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default NotActivatedModal;
